import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
    EmailShareButton,
    EmailIcon,
    FacebookShareButton,
    FacebookIcon,
    TwitterShareButton,
    TwitterIcon,
    WhatsappShareButton,
    WhatsappIcon,
} from 'react-share';
import config from '../../../config.json';
import './ShareOptionsModalV2.css';

export function resolveShareImageUrl(imageUrl) {
    if (!imageUrl) return '';
    const trimmed = String(imageUrl).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const base = String(config.SERVER_URL_IMG || '').replace(/\/?$/, '/');
    const path = trimmed.replace(/^\//, '');
    return `${base}${path
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/')}`;
}

export function resolveSharePageUrl(webAlias) {
    const alias = String(webAlias || '').trim().replace(/^\/+/, '');
    if (alias) return `https://myoola.oolalife.com/${alias}`;
    return 'https://www.oolalife.com';
}

/**
 * Dashboard / Portal V2 share sheet. Does not modify classic ShareOptionsModal.
 * Social crawlers preview whatever URL we pass — so WhatsApp/Facebook/Twitter
 * get the generated badge PNG instead of the ambassador landing-page logo.
 */
const ShareOptionsModalV2 = ({
    setActiveShare,
    imageUrl = '',
    text = '',
    webAlias = '',
}) => {
    const pageUrl = useMemo(() => resolveSharePageUrl(webAlias), [webAlias]);
    const imageAbs = useMemo(
        () => resolveShareImageUrl(imageUrl),
        [imageUrl]
    );
    const previewUrl = imageAbs || pageUrl;
    const shareText = useMemo(() => {
        const raw = String(text || '').trim();
        if (!raw) return pageUrl;
        if (raw.includes(pageUrl)) return raw;
        return `${raw} ${pageUrl}`;
    }, [text, pageUrl]);
    const canNativeShare =
        typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setActiveShare(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [setActiveShare]);

    const handleNativeShare = async () => {
        try {
            await navigator.share({
                title: 'Oola',
                text: shareText,
                url: previewUrl,
            });
        } catch (err) {
            if (err && err.name !== 'AbortError') {
                // Fall through to the icon buttons if the sheet is unavailable.
            }
        }
    };

    return ReactDOM.createPortal(
        <div
            className="dv2-share"
            role="presentation"
            onClick={() => setActiveShare(false)}
        >
            <div
                className="dv2-share__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dv2-share-title"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="dv2-share__close"
                    aria-label="Close"
                    onClick={() => setActiveShare(false)}
                >
                    ×
                </button>
                <p className="dv2-share__kicker">Share</p>
                <h3 id="dv2-share-title" className="dv2-share__title">
                    Share your badge
                </h3>
                <p className="dv2-share__hint">
                    Friends will see the badge you earned — not the default Oola
                    logo.
                </p>
                {imageAbs ? (
                    <div className="dv2-share__preview">
                        <img
                            src={imageAbs}
                            alt="Badge you earned"
                        />
                    </div>
                ) : null}
                {shareText ? (
                    <p className="dv2-share__msg">{shareText}</p>
                ) : null}
                {canNativeShare ? (
                    <button
                        type="button"
                        className="dv2-share__native"
                        onClick={handleNativeShare}
                    >
                        Share via apps
                    </button>
                ) : null}
                <div className="dv2-share__social">
                    <FacebookShareButton
                        url={previewUrl}
                        quote={shareText}
                        hashtag="#OolaLife"
                    >
                        <FacebookIcon round size={48} />
                    </FacebookShareButton>
                    <TwitterShareButton
                        url={previewUrl}
                        title={shareText}
                        hashtags={['OolaLife']}
                    >
                        <TwitterIcon round size={48} />
                    </TwitterShareButton>
                    <WhatsappShareButton
                        url={previewUrl}
                        title={shareText}
                        separator=" "
                    >
                        <WhatsappIcon round size={48} />
                    </WhatsappShareButton>
                    <EmailShareButton url={pageUrl} body={shareText} subject="Oola">
                        <EmailIcon round size={48} />
                    </EmailShareButton>
                </div>
            </div>
        </div>,
        document.body
    );
};

ShareOptionsModalV2.propTypes = {
    setActiveShare: PropTypes.func.isRequired,
    imageUrl: PropTypes.string,
    text: PropTypes.string,
    webAlias: PropTypes.string,
};

ShareOptionsModalV2.defaultProps = {
    imageUrl: '',
    text: '',
    webAlias: '',
};

export default ShareOptionsModalV2;
